import { useIntl } from 'react-intl';

import Badge from 'src/componentes/Badge';
import { CORES_STATUS_BADGE } from 'src/utils/constants';

const FALLBACK_BG = CORES_STATUS_BADGE.cinza.corFundo;
const FALLBACK_TEXT = CORES_STATUS_BADGE.cinza.cor;

const badgeConfigs = {
  ativo: {
    backgroundColor: CORES_STATUS_BADGE.azul.corFundo,
    color: CORES_STATUS_BADGE.azul.cor,
    textId: 'ativo',
  },
  inativo: {
    backgroundColor: CORES_STATUS_BADGE.cinza.corFundo,
    color: CORES_STATUS_BADGE.cinza.cor,
    textId: 'inativo',
  },
  obrigatorio: {
    backgroundColor: CORES_STATUS_BADGE.laranja.corFundo,
    color: CORES_STATUS_BADGE.laranja.cor,
    textId: 'obrigatorio',
  },
  exportacao: {
    backgroundColor: '#DBEAFE',
    color: '#1E40AF',
    textId: 'exportacao',
  },
  armazenamentoNormal: {
    backgroundColor: CORES_STATUS_BADGE.verde.corFundo,
    color: CORES_STATUS_BADGE.verde.cor,
  },
  armazenamentoAtencao: {
    backgroundColor: CORES_STATUS_BADGE.laranja.corFundo,
    color: CORES_STATUS_BADGE.laranja.cor,
  },
  armazenamentoCritico: {
    backgroundColor: CORES_STATUS_BADGE.vermelho.corFundo,
    color: CORES_STATUS_BADGE.vermelho.cor,
  },
};

const normalizeOpaqueColor = (color) => {
  if (typeof color !== 'string') return color;

  const normalized = color.trim();

  // #RRGGBBAA -> #RRGGBB
  if (/^#([0-9a-f]{8})$/i.test(normalized)) {
    return `#${normalized.slice(1, 7)}`;
  }

  // #RGBA -> #RGB
  if (/^#([0-9a-f]{4})$/i.test(normalized)) {
    return `#${normalized.slice(1, 4)}`;
  }

  // rgba(r,g,b,a) -> rgb(r,g,b)
  const rgbaMatch = normalized.match(/^rgba\((.+)\)$/i);
  if (rgbaMatch?.[1]) {
    const parts = rgbaMatch[1]
      .split(',')
      .map((part) => part.trim())
      .slice(0, 3);
    if (parts.length === 3) return `rgb(${parts.join(', ')})`;
  }

  // hsla(h,s,l,a) -> hsl(h,s,l)
  const hslaMatch = normalized.match(/^hsla\((.+)\)$/i);
  if (hslaMatch?.[1]) {
    const parts = hslaMatch[1]
      .split(',')
      .map((part) => part.trim())
      .slice(0, 3);
    if (parts.length === 3) return `hsl(${parts.join(', ')})`;
  }

  return normalized;
};

const parseRgbTuple = (color) => {
  if (typeof color !== 'string') return null;

  const normalized = normalizeOpaqueColor(color);

  const shortHexMatch = normalized.match(/^#([0-9a-f]{3})$/i);
  if (shortHexMatch?.[1]) {
    const [r, g, b] = shortHexMatch[1].split('');
    return {
      r: parseInt(`${r}${r}`, 16),
      g: parseInt(`${g}${g}`, 16),
      b: parseInt(`${b}${b}`, 16),
    };
  }

  const fullHexMatch = normalized.match(/^#([0-9a-f]{6})$/i);
  if (fullHexMatch?.[1]) {
    const value = fullHexMatch[1];
    return {
      r: parseInt(value.slice(0, 2), 16),
      g: parseInt(value.slice(2, 4), 16),
      b: parseInt(value.slice(4, 6), 16),
    };
  }

  const rgbMatch = normalized.match(
    /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i
  );
  if (rgbMatch) {
    return {
      r: Number(rgbMatch[1]),
      g: Number(rgbMatch[2]),
      b: Number(rgbMatch[3]),
    };
  }

  return null;
};

const createSoftBackground = (color, alpha = 0.16) => {
  const rgb = parseRgbTuple(color);
  if (!rgb) return color;

  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

const darkenColor = (color, factor = 0.72) => {
  const rgb = parseRgbTuple(color);
  if (!rgb) return color;

  const clamp = (value) => Math.max(0, Math.min(255, Math.round(value)));
  return `rgb(${clamp(rgb.r * factor)}, ${clamp(rgb.g * factor)}, ${clamp(
    rgb.b * factor
  )})`;
};

export default function StatusBadge({
  type,
  text,
  className,
  backgroundColor,
  textColor,
  style,
  children,
}) {
  const intl = useIntl();
  const config = badgeConfigs[type] || {};
  const hasCustomBackgroundColor = !!backgroundColor;
  const baseColor = normalizeOpaqueColor(
    backgroundColor || config.backgroundColor
  );
  const useSoftTone = hasCustomBackgroundColor && !textColor;
  const finalBackgroundColor =
    (useSoftTone ? createSoftBackground(baseColor) : baseColor) || FALLBACK_BG;
  const finalTextColor =
    textColor ||
    (useSoftTone ? darkenColor(baseColor) : config.color) ||
    FALLBACK_TEXT;

  const finalText =
    text || (config.textId ? intl.formatMessage({ id: config.textId }) : '');

  return (
    <Badge
      color=""
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 'fit-content',
        height: '24px',
        boxSizing: 'border-box',
        opacity: 1,
        border: 'none',
        borderRadius: '999px',
        padding: '2px 8px',
        fontWeight: '500',
        fontSize: '12px',
        lineHeight: '100%',
        fontFamily: 'Nunito',
        gap: '8px',
        backgroundColor: finalBackgroundColor,
        color: finalTextColor,
        ...style,
      }}
    >
      {children}
      {finalText}
    </Badge>
  );
}
